import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import MaxparPolicyDrawer from '../components/MaxparPolicyDrawer';
import GuaibimPolicyDrawer from '../components/GuaibimPolicyDrawer';
import api from '../services/api';
import { RefreshCw, Search, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const PRODUCTS = {
  resolve: 'CASA FÁCIL RESOLVE',
  'resolve-mais': 'CASA FÁCIL RESOLVE MAIS',
  'guaibim-resolve': 'GUAIBIM RESOLVE'
};

const MAXPAR_PRODUCTS = new Set(['resolve', 'resolve-mais']);

const STATUS_FILTERS = [
  { id: '', label: 'Todas' },
  { id: 'vigente', label: 'Vigentes' },
  { id: 'cancelada', label: 'Canceladas' },
  { id: 'vencida', label: 'Encerradas' },
  { id: 'aguardando', label: 'Aguardando' }
];

/**
 * Formats a stored calendar day for the collaborator.
 */
function formatDate(value) {
  if (!value) return '—';
  const [year, month, day] = String(value).split('-');
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

/**
 * Formats a CPF or CNPJ while keeping unknown lengths readable.
 */
function formatDocument(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return value || '—';
}

/**
 * Lets a collaborator check whether a customer's policy is still in force.
 */
function ProductCustomers() {
  const [params, setParams] = useSearchParams();
  const productId = params.get('product') || '';
  const productLabel = PRODUCTS[productId] || '';
  const [query, setQuery] = useState(params.get('q') || '');
  const [status, setStatus] = useState(params.get('status') || '');
  const [page, setPage] = useState(Number(params.get('page') || 1));
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const canQueryMaxpar = MAXPAR_PRODUCTS.has(productId);
  const canQueryLocal = productId === 'guaibim-resolve';

  useEffect(() => {
    setSelectedCustomer(null);
  }, [productId]);

  useEffect(() => {
    if (!productLabel) return undefined;
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const response = await api.get('/product-customers', {
          params: { product: productId, q: query, status, page }
        });
        if (active) setResult(response.data);
      } catch (error) {
        if (active) {
          toast.error(error.response?.data?.error || 'Erro ao consultar os clientes');
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [productId, productLabel, query, status, page]);

  /**
   * Rebuilds the base from the processed files and reloads the current search.
   */
  const refreshBase = async () => {
    setSyncing(true);
    try {
      await api.post('/product-customers/sync');
      const response = await api.get('/product-customers', {
        params: { product: productId, q: query, status, page }
      });
      setResult(response.data);
      toast.success('Base de clientes atualizada');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao atualizar a base');
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Applies a new search from the first page.
   */
  const submitSearch = (event) => {
    event.preventDefault();
    const nextQuery = event.target.elements.q.value.trim();
    setPage(1);
    setQuery(nextQuery);
    const next = new URLSearchParams(params);
    if (nextQuery) next.set('q', nextQuery);
    else next.delete('q');
    next.delete('page');
    setParams(next, { replace: true });
  };

  if (!productLabel) {
    return (
      <Layout headerTitle="Clientes" headerSubtitle="Consulta de vigência por produto">
        <div className="p-6 max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Clientes por produto</h1>
          <p className="text-gray-600 mb-6">Escolha o produto para consultar se a apólice do cliente está vigente.</p>
          <div className="grid gap-3">
            {Object.entries(PRODUCTS).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setParams({ product: id })}
                className="text-left bg-white border border-gray-200 rounded-lg px-4 py-3 hover:border-primary-300"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  const summary = result?.summary;
  const pages = result ? Math.max(1, Math.ceil(result.matched / result.pageSize)) : 1;

  return (
    <Layout headerTitle={productLabel} headerSubtitle="Consulta de clientes e vigência">
      <div className="p-6 max-w-6xl mx-auto space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{productLabel}</h1>
            <p className="text-gray-600">
              Consulte o cliente que entrou em contato. A situação usa a última adesão ou o último cancelamento processado.
              {canQueryMaxpar ? ' Clique no cliente para ver a apólice na MAXPAR.' : ''}
              {canQueryLocal ? ' Clique no cliente para ver a apólice na base local.' : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={refreshBase}
            disabled={syncing}
            className="inline-flex items-center gap-2 px-4 py-2 text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 disabled:opacity-60"
          >
            <RefreshCw className="h-4 w-4" />
            {syncing ? 'Atualizando...' : 'Atualizar base'}
          </button>
        </div>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SummaryCard label="Apólices" value={summary.total} />
            <SummaryCard label="Vigentes" value={summary.vigente} />
            <SummaryCard label="Canceladas" value={summary.cancelada} />
            <SummaryCard label="Encerradas" value={summary.vencida} />
          </div>
        )}

        <form onSubmit={submitSearch} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-col sm:flex-row gap-3">
          <label className="flex-1">
            <span className="block text-sm font-medium text-gray-700 mb-1">Nome, CPF ou apólice</span>
            <input
              name="q"
              defaultValue={query}
              placeholder="Ex.: Maria, 123.456.789-00 ou 005010003654993"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </label>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 sm:self-end"
          >
            <Search className="h-4 w-4" />
            Consultar
          </button>
        </form>

        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter.id || 'all'}
              type="button"
              onClick={() => {
                setStatus(filter.id);
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-full text-sm ${
                status === filter.id ? 'bg-primary-600 text-white' : 'bg-white border border-gray-200 text-gray-700'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <p className="px-6 py-8 text-sm text-gray-500">Consultando...</p>
          ) : !result || result.customers.length === 0 ? (
            <p className="px-6 py-8 text-sm text-gray-500">Nenhum cliente encontrado para essa consulta.</p>
          ) : (
            <ul className="divide-y divide-gray-200">
              {result.customers.map((customer) => (
                <li key={customer.policyNumber}>
                  <CustomerRow
                    customer={customer}
                    clickable={canQueryMaxpar || canQueryLocal}
                    onOpen={setSelectedCustomer}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        {canQueryMaxpar && (
          <MaxparPolicyDrawer
            customer={selectedCustomer}
            onClose={() => setSelectedCustomer(null)}
          />
        )}
        {canQueryLocal && (
          <GuaibimPolicyDrawer
            customer={selectedCustomer}
            onClose={() => setSelectedCustomer(null)}
          />
        )}

        {result && result.matched > result.pageSize && (
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Página {page} de {pages}</span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((current) => current - 1)}
                className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg disabled:opacity-50"
              >
                Anterior
              </button>
              <button
                type="button"
                disabled={page >= pages}
                onClick={() => setPage((current) => current + 1)}
                className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg disabled:opacity-50"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

/**
 * Shows one portfolio total above the search.
 */
function SummaryCard({ label, value }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-semibold text-gray-900">{value}</p>
    </div>
  );
}

/**
 * Renders one customer and opens the MAXPAR panel when the product is sent there.
 */
function CustomerRow({ customer, clickable, onOpen }) {
  const content = (
    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
      <div>
        <p className="text-lg font-semibold text-gray-900">{customer.customerName || 'Cliente sem nome'}</p>
        <p className="text-sm text-gray-600">
          CPF/CNPJ {formatDocument(customer.document)} · Apólice {customer.policyNumber}
        </p>
        <p className="text-sm text-gray-500 mt-1">
          {customer.operationLabel} em {formatDate(customer.eventDate)}
          {customer.startDate ? ` · Vigência ${formatDate(customer.startDate)}${customer.endDate ? ` a ${formatDate(customer.endDate)}` : ''}` : ''}
        </p>
        {(customer.city || customer.phone) && (
          <p className="text-sm text-gray-500">
            {[customer.city, customer.state].filter(Boolean).join(' / ')}
            {customer.phone ? ` · ${customer.phone}` : ''}
          </p>
        )}
        {customer.sourceName && (
          <p className="text-xs text-gray-400 mt-1">Arquivo {customer.sourceName}</p>
        )}
      </div>
      <StatusBadge customer={customer} />
    </div>
  );

  if (!clickable) {
    return <div className="px-6 py-4">{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(customer)}
      className="w-full px-6 py-4 text-left hover:bg-gray-50"
    >
      {content}
    </button>
  );
}

/**
 * States in plain language whether the customer is regular.
 */
function StatusBadge({ customer }) {
  const regular = customer.regular;
  return (
    <div className={`rounded-lg px-4 py-3 min-w-[220px] ${regular ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
      <div className="flex items-center gap-2 font-semibold">
        {regular ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
        {regular ? 'Regular' : 'Não regular'}
      </div>
      <p className="text-sm mt-1">{customer.statusLabel}</p>
    </div>
  );
}

export default ProductCustomers;
